import { Component } from '@theme/component';

/**
 * Slider della barra annunci di 120''LAB (sezione `lab-announcement-bar`).
 *
 * - Con due o piu messaggi scorre in orizzontale a loop infinito: prima e ultima slide vengono
 *   clonate ai due estremi della lista e la lista si sposta con `translate3d`. A fine corsa, se
 *   si e arrivati su un clone, la lista salta senza transizione sulla slide vera corrispondente.
 *   Con `data-transition="fade"` le slide restano sovrapposte e si alternano in dissolvenza.
 * - Autoplay ogni `data-speed` secondi, in pausa con il mouse sopra, con il focus da tastiera
 *   dentro la barra, a scheda nascosta, durante il trascinamento e quando un blocco e
 *   selezionato nell'editor del tema.
 * - Swipe con il dito e trascinamento con il mouse. Nessuna freccia, nessun pallino.
 * - `aria-hidden` e `inert` sulle slide non visibili (e sui cloni): niente focus su link fuori vista.
 * - `prefers-reduced-motion`: niente animazione (le slide cambiano di colpo), la rotazione resta.
 * - Con un solo messaggio la sezione non carica questo file e la barra resta statica.
 *
 * @typedef {'slide' | 'fade'} TransitionMode
 *
 * @typedef {object} DragState
 * @property {number} pointerId
 * @property {number} startX
 * @property {number} startY
 * @property {number} startTime
 * @property {number} deltaX
 * @property {boolean} active
 *
 * @extends {Component}
 */
class LabAnnouncementSlider extends Component {
  /** Durata della transizione, in ms: deve coincidere con il CSS della sezione. */
  static DURATION = 400;

  /** Movimento minimo, in px, prima che un gesto diventi trascinamento. */
  static DRAG_START = 6;

  /** Distanza minima, in px, per cambiare slide con uno swipe lento. */
  static SWIPE_DISTANCE = 40;

  /** @type {HTMLElement | null} */
  #list = null;

  /** Slide vere (senza cloni), nell'ordine del DOM. @type {HTMLElement[]} */
  #slides = [];

  /** @type {TransitionMode} */
  #mode = 'slide';

  #autoplay = false;

  /** Intervallo tra due slide, in ms. */
  #interval = 3000;

  /** Indice della slide visibile, 0..n-1. */
  #index = 0;

  /** Posizione nella lista. In modalita `slide` va da 0 (clone dell'ultima) a n+1 (clone della prima). */
  #position = 0;

  #ready = false;

  /** True mentre una transizione e in corso: i comandi ricevuti nel frattempo vengono ignorati. */
  #animating = false;

  /** @type {number | undefined} */
  #autoplayTimer = undefined;

  /** @type {number | undefined} */
  #settleTimer = undefined;

  /** Motivi per cui l'autoplay e in pausa (hover, focus, hidden, drag, editor). @type {Set<string>} */
  #pauseReasons = new Set();

  /** @type {DragState | null} */
  #drag = null;

  /** Fino a questo istante (performance.now) i click vengono scartati: seguono un trascinamento. */
  #suppressClickUntil = 0;

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.#setup, { once: true });
    } else {
      this.#setup();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('DOMContentLoaded', this.#setup);
    this.#teardown();
  }

  /**
   * Chiamato da `morph` quando un nuovo rendering della sezione cambia il contenuto: il nuovo
   * HTML arriva senza cloni, quindi il loop va ricostruito da capo.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#teardown();
    this.#setup();
  }

  /** Passa alla slide successiva. */
  next() {
    this.#step(1);
  }

  /** Torna alla slide precedente. */
  previous() {
    this.#step(-1);
  }

  #setup = () => {
    if (this.#ready) return;

    const list = this.querySelector('.lab-announcement-bar__list');
    if (!(list instanceof HTMLElement)) return;

    // Cloni rimasti da un'inizializzazione precedente (HTML serializzato dal DOM vivo): si rifanno da zero.
    list.querySelectorAll('[data-lab-clone]').forEach((clone) => clone.remove());

    const slides = Array.from(list.children).filter((child) => child instanceof HTMLElement);
    if (slides.length < 2) return;

    this.#list = list;
    this.#slides = /** @type {HTMLElement[]} */ (slides);
    this.#mode = this.dataset.transition === 'fade' ? 'fade' : 'slide';
    this.#autoplay = this.dataset.autoplay === 'true';

    const seconds = Number.parseFloat(this.dataset.speed ?? '');
    this.#interval = (Number.isFinite(seconds) && seconds > 0 ? seconds : 3) * 1000;

    this.#index = 0;
    this.#position = 0;
    this.#animating = false;

    if (this.#mode === 'slide') {
      const first = this.#slides[0];
      const last = this.#slides[this.#slides.length - 1];
      if (!first || !last) return;

      list.prepend(this.#createClone(last));
      list.append(this.#createClone(first));
      // Salto senza transizione: la lista parte dalla prima slide vera, non dal clone dell'ultima.
      this.#jumpTo(1);
    }

    this.#updateSlideStates();
    this.setAttribute('data-ready', '');
    this.#ready = true;

    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerup', this.#onPointerEnd);
    this.addEventListener('pointercancel', this.#onPointerEnd);
    this.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    this.addEventListener('dragstart', this.#onDragStart);
    this.addEventListener('click', this.#onClick, true);
    list.addEventListener('transitionend', this.#onTransitionEnd);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.#onBlockDeselect);

    if (document.hidden) this.#pauseReasons.add('hidden');
    this.#schedule();
  };

  #teardown() {
    if (!this.#ready) return;

    this.#ready = false;
    clearTimeout(this.#autoplayTimer);
    clearTimeout(this.#settleTimer);
    this.#pauseReasons.clear();
    this.#drag = null;

    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerup', this.#onPointerEnd);
    this.removeEventListener('pointercancel', this.#onPointerEnd);
    this.removeEventListener('pointerenter', this.#onPointerEnter);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.removeEventListener('dragstart', this.#onDragStart);
    this.removeEventListener('click', this.#onClick, true);
    this.#list?.removeEventListener('transitionend', this.#onTransitionEnd);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#onBlockDeselect);

    // Se l'elemento viene riattaccato al DOM, #setup ricrea i cloni: qui si tolgono quelli vecchi.
    this.#list?.querySelectorAll('[data-lab-clone]').forEach((clone) => clone.remove());
    this.#list?.style.removeProperty('transform');
    this.#list?.style.removeProperty('transition');
    this.removeAttribute('data-ready');
  }

  /**
   * Copia di una slide per il loop: fuori dall'albero di accessibilita, non focalizzabile e
   * senza gli attributi con cui l'editor del tema riconosce i blocchi.
   * @param {HTMLElement} slide
   * @returns {HTMLElement}
   */
  #createClone(slide) {
    const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));

    clone.setAttribute('data-lab-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');

    for (const element of [clone, ...clone.querySelectorAll('[id], [data-shopify-editor-block]')]) {
      element.removeAttribute('id');
      element.removeAttribute('data-shopify-editor-block');
    }

    return clone;
  }

  /**
   * Sposta la lista sulla posizione corrente (in modalita `slide`), con un eventuale
   * scostamento in px per seguire il dito o il mouse.
   * @param {number} offset
   */
  #applyTransform(offset) {
    if (!this.#list) return;

    this.#list.style.transform = `translate3d(calc(${-this.#position * 100}% + ${offset}px), 0, 0)`;
  }

  /** Allinea `aria-hidden` e `inert` delle slide vere con la slide visibile. */
  #updateSlideStates() {
    this.#slides.forEach((slide, index) => {
      const visible = index === this.#index;

      slide.setAttribute('aria-hidden', String(!visible));
      slide.toggleAttribute('inert', !visible);
    });
  }

  /**
   * Cambia slide con animazione. Ignorato mentre un'altra transizione e in corso.
   * @param {number} direction 1 = avanti, -1 = indietro
   */
  #step(direction) {
    if (!this.#ready) return;

    if (this.#animating) {
      this.#schedule();
      return;
    }

    const count = this.#slides.length;
    this.#index = (((this.#index + direction) % count) + count) % count;

    if (this.#mode === 'slide') {
      this.#position += direction;
      this.#applyTransform(0);
      this.#updateSlideStates();
      this.#animating = true;

      clearTimeout(this.#settleTimer);
      if (this.#reducedMotion.matches) {
        this.#settle();
      } else {
        // Rete di sicurezza: se `transitionend` non arriva (scheda in background, ecc.)
        this.#settleTimer = window.setTimeout(this.#settle, LabAnnouncementSlider.DURATION + 100);
      }
    } else {
      this.#updateSlideStates();
    }

    this.#schedule();
  }

  /**
   * A fine corsa: se la lista e su un clone salta, senza transizione, sulla slide vera corrispondente.
   */
  #settle = () => {
    clearTimeout(this.#settleTimer);
    this.#animating = false;

    if (this.#mode !== 'slide' || !this.#list) return;

    const count = this.#slides.length;
    if (this.#position === 0) {
      this.#position = count;
    } else if (this.#position === count + 1) {
      this.#position = 1;
    } else {
      return;
    }

    this.#jumpTo(this.#position);
  };

  /**
   * Posiziona la lista senza animazione e riabilita le transizioni subito dopo.
   * @param {number} position
   */
  #jumpTo(position) {
    const list = this.#list;
    if (!list) return;

    this.#position = position;
    list.style.transition = 'none';
    this.#applyTransform(0);
    // Forza il calcolo dello stile, altrimenti la transizione verrebbe riattivata prima del salto.
    void list.offsetWidth;
    list.style.removeProperty('transition');
  }

  /**
   * Va alla slide indicata senza animazione (usato dall'editor del tema).
   * @param {number} index
   */
  #goTo(index) {
    clearTimeout(this.#settleTimer);
    this.#animating = false;
    this.#index = index;

    if (this.#mode === 'slide') this.#jumpTo(index + 1);
    this.#updateSlideStates();
  }

  /** @param {string} reason */
  #pause(reason) {
    this.#pauseReasons.add(reason);
    clearTimeout(this.#autoplayTimer);
  }

  /** @param {string} reason */
  #resume(reason) {
    this.#pauseReasons.delete(reason);
    this.#schedule();
  }

  /** Riarma il timer dell'autoplay, se non ci sono motivi di pausa. */
  #schedule() {
    clearTimeout(this.#autoplayTimer);

    if (!this.#ready || !this.#autoplay || this.#pauseReasons.size > 0) return;

    this.#autoplayTimer = window.setTimeout(() => this.#step(1), this.#interval);
  }

  /** @param {TransitionEvent} event */
  #onTransitionEnd = (event) => {
    if (event.target !== this.#list || event.propertyName !== 'transform') return;

    this.#settle();
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (!event.isPrimary || this.#animating) return;

    this.#drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startTime: event.timeStamp,
      deltaX: 0,
      active: false,
    };
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    const deltaX = event.clientX - drag.startX;

    if (!drag.active) {
      if (Math.abs(deltaX) < LabAnnouncementSlider.DRAG_START) return;

      // Gesto piu verticale che orizzontale: e uno scroll della pagina, non uno swipe.
      if (Math.abs(event.clientY - drag.startY) > Math.abs(deltaX)) {
        this.#drag = null;
        return;
      }

      drag.active = true;
      this.#pause('drag');
      try {
        this.#list?.setPointerCapture(event.pointerId);
      } catch {
        // Puntatore gia rilasciato: il trascinamento prosegue senza cattura.
      }
      if (this.#mode === 'slide' && this.#list) this.#list.style.transition = 'none';
    }

    // Il clone disponibile ai lati e uno solo: il trascinamento non supera una slide (= larghezza della lista).
    const limit = this.#list ? this.#list.offsetWidth : 0;
    drag.deltaX = limit ? Math.max(-limit, Math.min(limit, deltaX)) : deltaX;

    if (this.#mode === 'slide') this.#applyTransform(drag.deltaX);
  };

  /** @param {PointerEvent} event */
  #onPointerEnd = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    this.#drag = null;
    if (!drag.active) return;

    // Dopo un trascinamento il browser puo generare un click sul link: va scartato.
    this.#suppressClickUntil = performance.now() + 150;

    const distance = Math.abs(drag.deltaX);
    const elapsed = Math.max(event.timeStamp - drag.startTime, 1);
    const flick = distance >= 12 && distance / elapsed >= 0.4;
    const commit = event.type === 'pointerup' && (distance >= LabAnnouncementSlider.SWIPE_DISTANCE || flick);

    if (this.#mode === 'slide' && this.#list) {
      this.#list.style.removeProperty('transition');
      // Senza cambio slide la lista torna al punto di partenza con la transizione normale.
      if (!commit) this.#applyTransform(0);
    }

    if (commit) this.#step(drag.deltaX < 0 ? 1 : -1);

    this.#resume('drag');
  };

  /** @param {PointerEvent} event */
  #onPointerEnter = (event) => {
    if (event.pointerType === 'mouse') this.#pause('hover');
  };

  #onPointerLeave = () => this.#resume('hover');

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    // Solo il focus da tastiera mette in pausa: un click del mouse sul link non deve fermare la barra.
    if (event.target instanceof Element && event.target.matches(':focus-visible')) this.#pause('focus');
  };

  #onFocusOut = () => this.#resume('focus');

  /** @param {Event} event */
  #onDragStart = (event) => event.preventDefault();

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (performance.now() >= this.#suppressClickUntil) return;

    event.preventDefault();
    event.stopPropagation();
  };

  #onVisibilityChange = () => {
    if (document.hidden) {
      this.#pause('hidden');
    } else {
      this.#resume('hidden');
    }
  };

  /** Editor del tema: selezionare un blocco mostra la sua slide e ferma la rotazione. */
  #onBlockSelect = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;

    const index = this.#slides.findIndex((slide) => slide === target || slide.contains(target));
    if (index === -1) return;

    this.#pause('editor');
    this.#goTo(index);
  };

  #onBlockDeselect = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;

    this.#resume('editor');
  };
}

if (!customElements.get('lab-announcement-slider')) {
  customElements.define('lab-announcement-slider', LabAnnouncementSlider);
}
